/**
 * @file index.tsx
 *
 * @fileoverview Renders bookmark UI and offer bookmarking
 * functionality.
 */

import React from "react";
import StarIcon from "@jq-icons/Star";
import StarLineIcon from "@jq-icons/StarLine";
import Badge from "../03-badge";
import styles from "./bookmark.module.scss";
import { motion, MotionProps } from "framer-motion";
// Define a new interface that extends MotionProps
type MotionButtonProps = MotionProps & React.HTMLAttributes<HTMLButtonElement>;
const CustomMotion = motion<MotionButtonProps>("button");

export interface BookmarkProps {
  /**
   * Displayed text or hover title.
   */
  text: string;
  /**
   * The is bookmarked flag.
   */
  isBookmarked: boolean;
  /**
   * On bookmark callback.
   */
  onBookmark: () => void;
  /**
   * The UI variant.
   *
   * @default 'icon'
   */
  variant?: "badge" | "icon";
}

export const Bookmark = ({
  text,
  isBookmarked,
  onBookmark,
  variant = "icon",
}: BookmarkProps) => {
  if (variant === "badge")
    return (
      <button
        className={styles.badgeButton}
        data-testid="badge-button"
        onClick={(e) => {
          e.stopPropagation();
          onBookmark();
        }}
        title={text}
      >
        <Badge
          badgeStyle={isBookmarked ? "primary" : "secondaryVeryLight"}
          className={styles.badge}
        >
          <StarIcon />
          <span>{text}</span>
        </Badge>
      </button>
    );

  return (
    <CustomMotion
      whileHover={{
        scale: 1.15,
      }}
      whileTap={{ scale: 0.9 }}
      data-testid="icon-button"
      onClick={(e) => {
        e.stopPropagation();
        onBookmark();
      }}
      title={text}
      className={styles.iconWrapper}
    >
      {isBookmarked ? (
        <StarIcon data-testid="star-icon" />
      ) : (
        <StarLineIcon data-testid="star-line-icon" />
      )}
    </CustomMotion>
  );
};

export default Bookmark;
